import { Result, Schema } from "effect";
import { UniqueIdentityValues } from "../../identity-input.ts";
import { OwnPropertyDescriptors } from "../../property-inspection.ts";
import { canonicalJsonStringify as serialize } from "../../canonical-json/serialize.ts";
import { canonicalJsonCause } from "../../canonical-json/cause.ts";
import { developmentIdentifier } from "../../development-identifiers.ts";
import { stationPath } from "../../station-path.ts";
import { sha256Utf8 } from "../../synchronous-sha256.ts";
import { ContractDecodeError } from "../../errors/validation.ts";
import {
  REVIEW_MANIFEST_VERSION, REVIEW_POLICY_VERSION, SPECIALIST_ROLES, SpecialistRole,
  ReviewRunState, RiskTier, ReviewToken, ReviewDigest, ReviewGlob, ReviewPolicyFileSemanticKind,
  changeReviewError, type ChangeReviewError, type ReviewManifest, type ReviewManifestIdentity,
  type ReviewManifestInput, type ReviewCoverageRecord, type ReviewCoverageRecordInput,
  type ReviewRiskPolicy, type ReviewRiskRule, type ReviewPolicyFileInput, type ReviewPolicyFileDecision
} from "./model.ts";

// Callback boundaries preserve the original descriptor/read order. Their
// refusal stays inside the invoking Result, including opaque platform causes.
const parseBoundary = <S extends Schema.ConstraintDecoder<unknown>>(
  schema: S, message?: string
) => {
  const decode = Schema.decodeUnknownResult(schema);
  return (value: unknown): S["Type"] => Result.getOrThrowWith(decode(value), error =>
    message === undefined ? new ContractDecodeError({ message: error.message }) : new TypeError(message));
};
const canonicalJsonStringify = (value: unknown) => Result.getOrThrowWith(serialize(value), canonicalJsonCause);

const MANIFEST_FIELDS = Object.freeze([
  "candidateId", "candidateCommitId", "effectiveTreeId", "policyDigest",
  "riskInputDigest", "riskTier", "riskSignals", "coverage"
]);

const MAX_REVIEW_POLICY_PATTERNS = 100;
const MAX_REVIEW_POLICY_FILES = 10_000;
const INTRINSIC_PROTECTED_PATTERNS = Object.freeze([
  "**/migrations/**",
  "**/wrangler.json",
  "**/wrangler.jsonc",
  "**/wrangler.toml",
  "**/cloudflare.config.ts"
]);

const PlainDataRecord = /* @__PURE__ */ (() => Schema.declare((value): value is Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null && !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype
))();
const PlainDataArray = /* @__PURE__ */ (() => Schema.declare((value): value is readonly unknown[] =>
  Array.isArray(value) && Object.getPrototypeOf(value) === Array.prototype
))();
const EnumerableDataProperty = /* @__PURE__ */ (() => Schema.Struct({ enumerable: Schema.Literal(true), value: Schema.Unknown }))();
const ArrayPropertyKeys = /* @__PURE__ */ (() => Schema.Array(Schema.Union([
  Schema.Literal("length"), Schema.String.check(Schema.isPattern(/^(?:0|[1-9][0-9]*)$/))
])))();
const ownDescriptors = /* @__PURE__ */ (() => parseBoundary(OwnPropertyDescriptors))();

function plainDataRecord(
  value: unknown,
  allowedKeys: readonly string[],
  description: string
): Readonly<Record<string, unknown>> {
  const input = parseBoundary(PlainDataRecord,
    `${description} must be a plain data object.`)(value);
  const fieldName = parseBoundary(Schema.Literals(allowedKeys),
    `${description} contains an unexpected field.`);
  const dataProperty = parseBoundary(EnumerableDataProperty,
    `${description} must not contain accessors.`);
  const descriptors = ownDescriptors(input);
  for (const key of Reflect.ownKeys(input)) {
    const field = fieldName(key);
    dataProperty(descriptors[field]);
  }
  return input;
}

function boundedDataArray(value: unknown, maximum: number, description: string): readonly unknown[] {
  const input = parseBoundary(PlainDataArray.check(Schema.makeFilter(array => !(array.length > maximum))),
    `${description} must be a bounded plain array.`)(value);
  const dataProperty = parseBoundary(EnumerableDataProperty,
    `${description} must contain plain data values.`);
  const descriptors = ownDescriptors(input);
  const values: unknown[] = [];
  for (let index = 0; index < input.length; index += 1) {
    values.push(dataProperty(descriptors[String(index)]).value);
  }
  parseBoundary(ArrayPropertyKeys,
    `${description} contains an unexpected field.`)(Reflect.ownKeys(input));
  return Object.freeze(values);
}

const uniqueValues = (values: readonly unknown[], description: string) =>
  parseBoundary(UniqueIdentityValues, description)(values);
function uniqueSortedTokens(values: readonly unknown[] | undefined, description: string): string[] {
  if (!values) return [];
  const token = parseBoundary(ReviewToken,
    `${description} contains an invalid token.`);
  const parsed = values.map(value => token(value));
  uniqueValues(parsed, `${description} must not contain duplicates.`);
  return parsed.sort();
}
const digest = (value: unknown, description: string): string =>
  parseBoundary(ReviewDigest, `${description} must be a SHA-256 digest.`)(value);
const reviewGlob = (value: unknown, description: string): string =>
  parseBoundary(ReviewGlob, `${description} contains an invalid bounded glob.`)(value);

function reviewGlobs(values: unknown, description: string): string[] {
  const globs = boundedDataArray(values, MAX_REVIEW_POLICY_PATTERNS, description)
    .map(value => reviewGlob(value, description));
  uniqueValues(globs, `${description} must not contain duplicates.`);
  return globs.sort();
}

const coverageRole = /* @__PURE__ */ (() => parseBoundary(SpecialistRole, "Review coverage contains an unknown specialist role."))();
const coverageRequired = /* @__PURE__ */ (() => parseBoundary(Schema.Boolean, "Review coverage required must be boolean."))();
const coverageState = /* @__PURE__ */ (() => parseBoundary(ReviewRunState, "Review coverage contains an unknown run state."))();
const manifestRiskTier = /* @__PURE__ */ (() => parseBoundary(RiskTier, "Review manifest contains an unknown risk tier."))();
const baselineRequired = /* @__PURE__ */ (() => parseBoundary(Schema.Literal(true), "Review coverage requires baseline correctness."))();
const queuedCoverage = /* @__PURE__ */ (() => parseBoundary(Schema.Array(Schema.Literal("queued")), "Review manifest coverage must begin queued."))();
const manifestVersion = /* @__PURE__ */ (() => parseBoundary(Schema.Literal(REVIEW_MANIFEST_VERSION), "Review manifest version is unsupported."))();
const policyRuleIdentity = /* @__PURE__ */ (() => parseBoundary(Schema.Struct({ id: ReviewToken, tier: RiskTier }), "Review policy rule has an invalid id or tier."))();
const policyRoles = /* @__PURE__ */ (() => parseBoundary(Schema.Array(SpecialistRole), "Review policy rule contains an unknown role."))();
const policyVersion = /* @__PURE__ */ (() => parseBoundary(Schema.Literal(REVIEW_POLICY_VERSION), "Review file policy input is invalid."))();
const generatedMarker = /* @__PURE__ */ (() => parseBoundary(Schema.UndefinedOr(Schema.Boolean), "Review policy generated marker must be boolean."))();
const semanticFileKind = /* @__PURE__ */ (() => parseBoundary(Schema.UndefinedOr(ReviewPolicyFileSemanticKind), "Review policy semantic kind is invalid."))();

function globExpression(pattern: string): RegExp {
  let expression = "^";
  for (let index = 0; index < pattern.length; index += 1) {
    const character = pattern.charAt(index);
    if (character === "*") {
      if (pattern[index + 1] === "*") {
        if (pattern[index + 2] === "/") {
          expression += "(?:.*/)?";
          index += 2;
        } else {
          expression += ".*";
          index += 1;
        }
      } else {
        expression += "[^/]*";
      }
    } else if (character === "?") {
      expression += "[^/]";
    } else {
      expression += character.replace(/[.+^${}()|[\]\\]/g, "\\$&");
    }
  }
  return new RegExp(`${expression}$`);
}

function coverageRecord(input: unknown): ReviewCoverageRecord {
  const boundary = plainDataRecord(
    input,
    ["role", "required", "files", "domains", "state"],
    "Review coverage"
  );
  const role = coverageRole(boundary.role);
  const required = coverageRequired(boundary.required);
  const state = coverageState(boundary.state ?? "queued");
  const files = boundedDataArray(
    boundary.files ?? [],
    MAX_REVIEW_POLICY_FILES,
    "Review coverage files"
  ).map((path) => stationPath(path)).sort();
  uniqueValues(files, "Review coverage files must not contain duplicates.");
  return Object.freeze({
    role,
    required,
    files: Object.freeze(files),
    domains: Object.freeze(
      uniqueSortedTokens(
        boundedDataArray(
          boundary.domains ?? [],
          MAX_REVIEW_POLICY_PATTERNS,
          "Review coverage domains"
        ),
        "Review coverage domains"
      )
    ),
    state
  });
}

function manifestIdentity(
  input: ReviewManifestInput
): Omit<ReviewManifest, "manifestDigest"> {
  const boundary = plainDataRecord(
    input,
    MANIFEST_FIELDS,
    "Review manifest"
  );
  const candidateId = developmentIdentifier(
    "candidate",
    boundary.candidateId
  );
  const candidateCommitId = developmentIdentifier(
    "commit",
    boundary.candidateCommitId
  );
  const effectiveTreeId = developmentIdentifier(
    "tree",
    boundary.effectiveTreeId
  );
  const policyDigest = digest(boundary.policyDigest, "Review policy digest");
  const riskInputDigest = digest(
    boundary.riskInputDigest,
    "Review risk input digest"
  );
  const riskTier = manifestRiskTier(boundary.riskTier);

  const coverage = boundedDataArray(
    boundary.coverage,
    SPECIALIST_ROLES.length,
    "Review manifest coverage"
  ).map(coverageRecord)
    .sort((left, right) => left.role.localeCompare(right.role));
  uniqueValues(coverage.map(record => record.role), "Review coverage must contain each specialist once.");
  baselineRequired(coverage.find(record => record.role === "baseline-correctness")?.required);
  queuedCoverage(coverage.map(record => record.state));

  return Object.freeze({
    version: REVIEW_MANIFEST_VERSION,
    candidateId,
    candidateCommitId,
    effectiveTreeId,
    policyDigest,
    riskInputDigest,
    riskTier,
    riskSignals: Object.freeze(
      uniqueSortedTokens(
        boundedDataArray(
          boundary.riskSignals,
          MAX_REVIEW_POLICY_PATTERNS,
          "Review risk signals"
        ),
        "Review risk signals"
      )
    ),
    coverage: Object.freeze(coverage)
  });
}

function createSync(
  input: ReviewManifestInput
): ReviewManifest {
  const identity = manifestIdentity(input);
  const manifestDigest = `sha256:${sha256Utf8(canonicalJsonStringify(identity))}`;
  return Object.freeze({ ...identity, manifestDigest });
}

function verify(value: unknown): ReviewManifest {
  const boundary = plainDataRecord(
    value,
    ["version", ...MANIFEST_FIELDS, "manifestDigest"],
    "Review manifest artifact"
  );
  manifestVersion(boundary.version);
  const expectedDigest = digest(
    boundary.manifestDigest,
    "Review manifest digest"
  );
  const verified = createSync({
    candidateId: boundary.candidateId as string,
    candidateCommitId: boundary.candidateCommitId as string,
    effectiveTreeId: boundary.effectiveTreeId as string,
    policyDigest: boundary.policyDigest as string,
    riskInputDigest: boundary.riskInputDigest as string,
    riskTier: boundary.riskTier as RiskTier,
    riskSignals: boundary.riskSignals as readonly string[],
    coverage: boundary.coverage as readonly ReviewCoverageRecordInput[]
  });
  parseBoundary(Schema.Literal(verified.manifestDigest),
    "Review manifest digest does not match its identity.")(expectedDigest);
  return verified;
}

function definePolicy(
  input: Omit<ReviewRiskPolicy, "version">
): ReviewRiskPolicy {
  const boundary = plainDataRecord(
    input,
    [
      "rules",
      "generatedFilePatterns",
      "vendorFilePatterns",
      "neverExcludePatterns"
    ],
    "Review risk policy"
  );
  const rules = boundedDataArray(
    boundary.rules,
    MAX_REVIEW_POLICY_PATTERNS,
    "Review risk policy rules"
  ).map((value) => {
    const rule = plainDataRecord(
      value,
      ["id", "tier", "signals", "requiredRoles", "advisoryRoles"],
      "Review risk policy rule"
    );
    const { id, tier } = policyRuleIdentity(rule);
    const requiredRoles = boundedDataArray(
      rule.requiredRoles,
      SPECIALIST_ROLES.length,
      "Review policy required roles"
    );
    const advisoryRoles = boundedDataArray(
      rule.advisoryRoles,
      SPECIALIST_ROLES.length,
      "Review policy advisory roles"
    );
    const roles = policyRoles([...requiredRoles, ...advisoryRoles]);
    return Object.freeze({
      id,
      tier,
      signals: Object.freeze(
        uniqueSortedTokens(
          boundedDataArray(
            rule.signals,
            MAX_REVIEW_POLICY_PATTERNS,
            "Review policy rule signals"
          ),
          "Rule signals"
        )
      ),
      requiredRoles: Object.freeze([...new Set(roles.slice(0, requiredRoles.length))].sort()),
      advisoryRoles: Object.freeze([...new Set(roles.slice(requiredRoles.length))].sort())
    });
  });
  uniqueValues(rules.map(rule => rule.id), "Review policy rule ids must be unique.");
  return Object.freeze({
    version: REVIEW_POLICY_VERSION,
    rules: Object.freeze(rules.sort((left, right) => left.id.localeCompare(right.id))),
    generatedFilePatterns: Object.freeze(
      reviewGlobs(boundary.generatedFilePatterns, "Generated file patterns")
    ),
    vendorFilePatterns: Object.freeze(
      reviewGlobs(boundary.vendorFilePatterns, "Vendor file patterns")
    ),
    neverExcludePatterns: Object.freeze(
      reviewGlobs(boundary.neverExcludePatterns, "Never-exclude patterns")
    )
  });
}

function applyPolicy(
  policy: ReviewRiskPolicy,
  files: readonly ReviewPolicyFileInput[]
): readonly ReviewPolicyFileDecision[] {
  const boundary = plainDataRecord(
    policy,
    [
      "version",
      "rules",
      "generatedFilePatterns",
      "vendorFilePatterns",
      "neverExcludePatterns"
    ],
    "Review file policy"
  );
  policyVersion(boundary.version);
  const validatedPolicy = definePolicy({
    rules: boundary.rules as readonly ReviewRiskRule[],
    generatedFilePatterns: boundary.generatedFilePatterns as readonly string[],
    vendorFilePatterns: boundary.vendorFilePatterns as readonly string[],
    neverExcludePatterns: boundary.neverExcludePatterns as readonly string[]
  });
  const generatedPatterns = reviewGlobs(
    validatedPolicy.generatedFilePatterns,
    "Generated file patterns"
  ).map((pattern) => [pattern, globExpression(pattern)] as const);
  const vendorPatterns = reviewGlobs(
    validatedPolicy.vendorFilePatterns,
    "Vendor file patterns"
  ).map((pattern) => [pattern, globExpression(pattern)] as const);
  const protectedPatterns = reviewGlobs(
    validatedPolicy.neverExcludePatterns,
    "Never-exclude patterns"
  ).map((pattern) => [pattern, globExpression(pattern)] as const);
  const intrinsicProtectedPatterns = INTRINSIC_PROTECTED_PATTERNS.map(
    (pattern) => [pattern, globExpression(pattern)] as const
  );
  const fileSnapshots = boundedDataArray(
    files,
    MAX_REVIEW_POLICY_FILES,
    "Review policy files"
  ).map((value) => {
    const file = plainDataRecord(
      value,
      ["path", "generated", "semanticKind"],
      "Review policy file"
    );
    const generated = generatedMarker(file.generated);
    const semanticKind = semanticFileKind(file.semanticKind) ?? "source";
    return Object.freeze({
      path: stationPath(file.path),
      generated: generated === true,
      semanticKind
    });
  });
  uniqueValues(fileSnapshots.map(file => file.path), "Review policy files must not contain duplicates.");
  const decisions = fileSnapshots.map((file): ReviewPolicyFileDecision => {
    const { path } = file;
    const protectedMatch = protectedPatterns.find(([, pattern]) =>
      pattern.test(path)
    );
    const intrinsicProtectedMatch = intrinsicProtectedPatterns.find(
      ([, pattern]) => pattern.test(path)
    );
    const semanticProtection =
      file.semanticKind === "source"
        ? undefined
        : `semantic:${file.semanticKind}`;
    const generatedMatch = generatedPatterns.find(([, pattern]) =>
      pattern.test(path)
    );
    const vendorMatch = vendorPatterns.find(([, pattern]) => pattern.test(path));
    const protection =
      semanticProtection ??
      intrinsicProtectedMatch?.[0] ??
      protectedMatch?.[0];
    if (protection && (file.generated || generatedMatch || vendorMatch)) {
      return Object.freeze({
        path,
        included: true,
        disposition: "protected-included",
        matchedPattern: protection
      });
    }
    if (file.generated || generatedMatch) {
      return Object.freeze({
        path,
        included: false,
        disposition: "generated-excluded",
        matchedPattern: generatedMatch?.[0] ?? "metadata:generated"
      });
    }
    if (vendorMatch) {
      return Object.freeze({
        path,
        included: false,
        disposition: "vendor-excluded",
        matchedPattern: vendorMatch[0]
      });
    }
    return Object.freeze({ path, included: true, disposition: "included" });
  });
  decisions.sort((left, right) =>
    left.path < right.path ? -1 : left.path > right.path ? 1 : 0
  );
  return Object.freeze(decisions);
}

/** Pure boundaries are synchronous Results; no host or runtime is hidden here. */
export const reviewManifestIdentity = (input: ReviewManifestInput): Result.Result<ReviewManifestIdentity, ChangeReviewError> =>
  Result.try({ try: () => manifestIdentity(input), catch: changeReviewError });
export const createReviewManifestSync = (input: ReviewManifestInput): Result.Result<ReviewManifest, ChangeReviewError> =>
  Result.try({ try: () => createSync(input), catch: changeReviewError });
export const verifyReviewManifest = (value: unknown): Result.Result<ReviewManifest, ChangeReviewError> =>
  Result.try({ try: () => verify(value), catch: changeReviewError });
export const defineReviewRiskPolicy = (input: Omit<ReviewRiskPolicy, "version">): Result.Result<ReviewRiskPolicy, ChangeReviewError> =>
  Result.try({ try: () => definePolicy(input), catch: changeReviewError });
export const applyReviewFilePolicy = (policy: ReviewRiskPolicy, files: readonly ReviewPolicyFileInput[]): Result.Result<readonly ReviewPolicyFileDecision[], ChangeReviewError> =>
  Result.try({ try: () => applyPolicy(policy, files), catch: changeReviewError });
